import React, { memo, type ReactElement, useEffect, useState } from 'react';
import {
  Box,
  Chip,
  Icon,
  Loader,
  TabContent,
  TabGroup,
  TabItem,
  Tooltip,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';
import { format, isValid } from 'date-fns';

import {
  SodAccessPath,
  SodApplicationGroup,
  SodFieldSet,
  SodRiskScore,
  SodSeverityChip,
} from '../../../../../shared/components';
import { CF_NEU_BLD, CF_SEC_MED } from '../../../../../shared/constants';
import {
  useInvertedLocalize,
  useTypedSelector,
} from '../../../../../shared/hooks';
import { shouldShowEntitlementTrustScore } from '../../../../../shared/utils';
import {
  type TrackedViolatingEntitlements,
  type TrackedViolationAssignedEntitlements,
} from '../../../data-layer/detailsTypes';

import './ViolationsTabDetailsConflictSP.css';

const SOD_DETAILS_CONFICT_SIDEPANEL_TABS = {
  SUMMARY: 'Summary',
  ACCESS_PATH: 'Access Path',
};

/**
 * ViolationsTabDetailsConflictSP
 */
function ViolationsTabDetailsConflictSP(): ReactElement {
  const l = useInvertedLocalize();
  const [activeTab, setActiveTab] = useState(
    SOD_DETAILS_CONFICT_SIDEPANEL_TABS.SUMMARY
  );

  const {
    global: { globalDateFormat },
    details: {
      violationsTab: {
        conflicts: { selectedRowData },
      },
    },
  } = useTypedSelector((state) => state.aag);

  useEffect(() => {
    setActiveTab(SOD_DETAILS_CONFICT_SIDEPANEL_TABS.SUMMARY);
  }, [selectedRowData?.id]);

  const renderLayout = () => {
    if (selectedRowData?.tracker.level === 0) {
      const isDescription = Boolean(
        selectedRowData.description !== null &&
          selectedRowData.description.trim().length !== 0
      );

      return (
        <Box
          tag='div'
          className='Sod-ViolationsTabDetailsConflictSP-layout--isFunction'>
          <Box
            tag='div'
            className='Sod-ViolationsTabDetailsConflictSP-layout--isCountWrapper'>
            <SodFieldSet
              label={l(
                'Number of {count, plural, one {Assigned Entitlement} other {Assigned Entitlements}}',
                { count: selectedRowData.assignedEntitlementsCount ?? 0 }
              )}
              value={selectedRowData.assignedEntitlementsCount ?? '-'}
              stringColor={CF_SEC_MED}
            />
            <Tooltip
              trigger={
                <Icon
                  kind='AlertInfoFilledSeparateColors'
                  color='info-700'
                  size='xSmall'
                />
              }
              text={l(
                'Entitlement assigned to the account that led to the violation of this function.'
              )}
              placement='bottom-end'
            />
          </Box>
          <SodFieldSet
            label={l('Description')}
            value={
              isDescription ? selectedRowData.description : l('Not Provided')
            }
            isNotProvided={!isDescription}
          />
          <SodFieldSet
            label={l('Account')}
            value={selectedRowData.accountName}
          />
          <SodFieldSet
            label={l('Application')}
            value={
              <SodApplicationGroup
                label={selectedRowData.endpointName}
                firstLogo={selectedRowData.endpointLogo ?? undefined}
              />
            }
          />
        </Box>
      );
    }

    if (selectedRowData?.tracker.level === 1) {
      const data =
        selectedRowData as unknown as TrackedViolationAssignedEntitlements;

      const isValidStartDate =
        data.startDate && isValid(new Date(data.startDate));
      const isValidEndDate = data.endDate && isValid(new Date(data.endDate));
      const isEntValue = Boolean(
        data.entitlementValue !== null &&
          data.entitlementValue.trim().length !== 0
      );
      const isEntType = Boolean(
        data.entitlementTypeName !== null &&
          data.entitlementTypeName.trim().length !== 0
      );
      const isDescription = Boolean(
        data.description !== null && data.description.trim().length !== 0
      );
      const isGlossary = Boolean(
        data.glossary !== null && data.glossary.trim().length !== 0
      );
      const isCustomProp1 = Boolean(
        data.customProperty1 !== null &&
          data.customProperty1.trim().length !== 0
      );

      return (
        <Box
          tag='div'
          className='Sod-ViolationsTabDetailsConflictSP-layout--isAssignedEntitlement'>
          {shouldShowEntitlementTrustScore(data.entitlementTrustScore) && (
            <Box
              tag='div'
              className='Sod-ViolationsTabDetailsConflictSP-layoutTrust'>
              <Typography kind='h4' color={CF_NEU_BLD}>
                {l('Trust')}
              </Typography>
              <SodRiskScore
                kind='critical'
                size='regular'
                value={data.entitlementTrustScore}
                shouldShowZero
                customChip={
                  <Chip
                    color='critical'
                    size='xSmall'
                    label={l('Low')}
                    leftIcon={<Icon kind='Sad' />}
                  />
                }
              />
              <span className='Sod-ViolationsTabDetailsConflictSP-layoutDivider' />
            </Box>
          )}
          <Box
            tag='div'
            className='Sod-ViolationsTabDetailsConflictSP-layoutDateWrapper'>
            <SodFieldSet
              label={l('Start')}
              value={
                isValidStartDate
                  ? format(
                      new Date(data.startDate as string | number),
                      globalDateFormat
                    )
                  : l('Not Provided')
              }
              isNotProvided={!isValidStartDate}
            />
            <SodFieldSet
              label={l('End')}
              value={
                isValidEndDate
                  ? format(
                      new Date(data.endDate as string | number),
                      globalDateFormat
                    )
                  : l('Not Provided')
              }
              isNotProvided={!isValidEndDate}
            />
            <span className='Sod-ViolationsTabDetailsConflictSP-layoutDivider' />
          </Box>
          <Box
            tag='div'
            className='Sod-ViolationsTabDetailsConflictSP-layout--isCountWrapper'>
            <SodFieldSet
              label={l('Number of Violating Entitlements')}
              value={data.totalViolatingEntitlements ?? '-'}
              stringColor={CF_SEC_MED}
            />
            <Tooltip
              trigger={
                <Icon
                  kind='AlertInfoFilledSeparateColors'
                  color='info-700'
                  size='xSmall'
                />
              }
              text={l('The direct cause of the violation of the function.')}
              placement='bottom-end'
            />
          </Box>
          <SodFieldSet
            label={l('Entitlement Value')}
            value={isEntValue ? data.entitlementValue : l('Not Provided')}
            isNotProvided={!isEntValue}
          />
          <SodFieldSet
            label={l('Entitlement Type')}
            value={isEntType ? data.entitlementTypeName : l('Not Provided')}
            isNotProvided={!isEntType}
          />
          <SodFieldSet
            label={l('Description')}
            value={isDescription ? data.description : l('Not Provided')}
            isNotProvided={!isDescription}
          />
          <SodFieldSet
            label={l('Glossary')}
            value={isGlossary ? data.glossary : l('Not Provided')}
            isNotProvided={!isGlossary}
          />
          <SodFieldSet
            label={l('Custom Property 1')}
            value={isCustomProp1 ? data.customProperty1 : l('Not Provided')}
            isNotProvided={!isCustomProp1}
          />
          <Box
            tag='div'
            className='Sod-ViolationsTabDetailsConflictSP-layoutCritcalChips'>
            <SodFieldSet
              label={l('Sox Critical')}
              value={<SodSeverityChip text={data.soxSeverity} />}
            />
            <SodFieldSet
              label={l('Sys Critical')}
              value={<SodSeverityChip text={data.sysSeverity} />}
            />
            <SodFieldSet
              label={l('Privileged')}
              value={<SodSeverityChip text={data.privilegedSeverity} />}
            />
          </Box>
        </Box>
      );
    }

    if (selectedRowData?.tracker.level === 2) {
      const data = selectedRowData as unknown as TrackedViolatingEntitlements;

      const isValidStartDate =
        data.startDate && isValid(new Date(data.startDate));
      const isValidEndDate = data.endDate && isValid(new Date(data.endDate));
      const isEntValue = Boolean(
        data.entitlementValue !== null &&
          data.entitlementValue.trim().length !== 0
      );
      const isEntType = Boolean(
        data.entitlementTypeName !== null &&
          data.entitlementTypeName.trim().length !== 0
      );
      const isDescription = Boolean(
        data.description !== null && data.description.trim().length !== 0
      );
      const isGlossary = Boolean(
        data.glossary !== null && data.glossary.trim().length !== 0
      );
      const isCustomProp1 = Boolean(
        data.customProperty1 !== null &&
          data.customProperty1.trim().length !== 0
      );

      return (
        <>
          <TabGroup
            value={activeTab}
            onChange={(tabValue: string) => setActiveTab(tabValue)}>
            <TabItem
              label={l(SOD_DETAILS_CONFICT_SIDEPANEL_TABS.SUMMARY)}
              value={SOD_DETAILS_CONFICT_SIDEPANEL_TABS.SUMMARY}
              icon={
                <Icon kind='Document' color='--color-foreground-neutral-bold' />
              }
            />
            <TabItem
              label={l(SOD_DETAILS_CONFICT_SIDEPANEL_TABS.ACCESS_PATH)}
              value={SOD_DETAILS_CONFICT_SIDEPANEL_TABS.ACCESS_PATH}
              icon={
                <Icon
                  kind='Hierarchy'
                  color='--color-foreground-neutral-bold'
                />
              }
            />
          </TabGroup>
          <TabContent
            value={activeTab}
            tabValue={SOD_DETAILS_CONFICT_SIDEPANEL_TABS.SUMMARY}
            className='Sod-ViolationsTabDetailsConflictSP-layout--isViolatingEntitlement'>
            <Box
              tag='div'
              className='Sod-ViolationsTabDetailsConflictSP-layout--isViolatingEntitlement'>
              {shouldShowEntitlementTrustScore(data.entitlementTrustScore) && (
                <Box
                  tag='div'
                  className='Sod-ViolationsTabDetailsConflictSP-layoutTrust'>
                  <Typography kind='h4' color={CF_NEU_BLD}>
                    {l('Trust')}
                  </Typography>
                  <SodRiskScore
                    kind='critical'
                    size='regular'
                    value={data.entitlementTrustScore}
                    shouldShowZero
                    customChip={
                      <Chip
                        color='critical'
                        size='xSmall'
                        label={l('Low')}
                        leftIcon={<Icon kind='Sad' />}
                      />
                    }
                  />
                  <span className='Sod-ViolationsTabDetailsConflictSP-layoutDivider' />
                </Box>
              )}
              <Box
                tag='div'
                className='Sod-ViolationsTabDetailsConflictSP-layoutDateWrapper'>
                <SodFieldSet
                  label={l('Start')}
                  value={
                    isValidStartDate
                      ? format(
                          new Date(data.startDate as string | number),
                          globalDateFormat
                        )
                      : l('Not Provided')
                  }
                  isNotProvided={!isValidStartDate}
                />
                <SodFieldSet
                  label={l('End')}
                  value={
                    isValidEndDate
                      ? format(
                          new Date(data.endDate as string | number),
                          globalDateFormat
                        )
                      : l('Not Provided')
                  }
                  isNotProvided={!isValidEndDate}
                />
                <span className='Sod-ViolationsTabDetailsConflictSP-layoutDivider' />
              </Box>
              <SodFieldSet
                label={l('Entitlement Value')}
                value={isEntValue ? data.entitlementValue : l('Not Provided')}
                isNotProvided={!isEntValue}
              />
              <SodFieldSet
                label={l('Entitlement Type')}
                value={isEntType ? data.entitlementTypeName : l('Not Provided')}
                isNotProvided={!isEntType}
              />
              <SodFieldSet
                label={l('Description')}
                value={isDescription ? data.description : l('Not Provided')}
                isNotProvided={!isDescription}
              />
              <SodFieldSet
                label={l('Glossary')}
                value={isGlossary ? data.glossary : l('Not Provided')}
                isNotProvided={!isGlossary}
              />
              <SodFieldSet
                label={l('Custom Property 1')}
                value={isCustomProp1 ? data.customProperty1 : l('Not Provided')}
                isNotProvided={!isCustomProp1}
              />
              <Box
                tag='div'
                className='Sod-ViolationsTabDetailsConflictSP-layoutCritcalChips'>
                <SodFieldSet
                  label={l('Sox Critical')}
                  value={<SodSeverityChip text={data.soxSeverity} />}
                />
                <SodFieldSet
                  label={l('Sys Critical')}
                  value={<SodSeverityChip text={data.sysSeverity} />}
                />
                <SodFieldSet
                  label={l('Privileged')}
                  value={<SodSeverityChip text={data.privilegedSeverity} />}
                />
              </Box>
            </Box>
          </TabContent>
          <TabContent
            value={activeTab}
            tabValue={SOD_DETAILS_CONFICT_SIDEPANEL_TABS.ACCESS_PATH}
            className='Sod-ViolationsTabDetailsConflictSP-layout--isViolatingEntitlementAccessPath'>
            <SodAccessPath
              violationId={data.violationId}
              assignedEntitlementId={data.assignedEntitlementId}
              violatingEntitlementId={String(data.entitlementKey)}
            />
          </TabContent>
        </>
      );
    }

    return (
      <Box
        tag='div'
        className='Sod-ViolationsTabDetailsConflictSP-layout--isLoading'>
        <Loader kind='dots' format='inline' color='brand' />
      </Box>
    );
  };

  const layoutWrapperClasses = cx(
    'Sod-ViolationsTabDetailsConflictSP-layout',
    selectedRowData &&
      `Sod-ViolationsTabDetailsConflictSP-layout--isLevel-${selectedRowData.tracker.level}`
  );

  return <Box className={layoutWrapperClasses}>{renderLayout()}</Box>;
}

export default memo(ViolationsTabDetailsConflictSP);
